import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchBranding, fetchMe, type Branding, type Me } from '../../api/client';
import { usePrefs } from '../../app/prefs';

export const ME_KEY = ['me'] as const;
export const BRANDING_KEY = ['branding'] as const;

export function useMe() {
  return useQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: Infinity, retry: 0 });
}

export function useBranding() {
  return useQuery({ queryKey: BRANDING_KEY, queryFn: fetchBranding, staleTime: 5 * 60_000 });
}

/** Chooses the logo variant for the active theme, falling back to what exists. */
export function logoFor(
  b: Branding | undefined,
  theme: 'light' | 'dark',
  kind: 'full' | 'mark',
): string | null {
  if (!b) return null;
  if (kind === 'mark')
    return b.logos.mark ?? (theme === 'dark' ? b.logos.dark : b.logos.light) ?? b.logos.light;
  return (theme === 'dark' ? b.logos.dark : b.logos.light) ?? b.logos.light ?? b.logos.dark;
}

/** Applies the server-side user preferences (authoritative) once the session is known. */
export function applyUserPrefs(me: Me) {
  usePrefs.getState().applyFromServer(me.theme, me.locale, me.nav_pinned ?? true, me.date_format ?? 'auto');
}

export function useSetMe() {
  const qc = useQueryClient();
  return (me: Me | null) => qc.setQueryData(ME_KEY, me);
}
